import { computed, watch, type Ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useGetApiSupervisionTimeCalculations, getApiSupervisionTimeCalculationsQueryKey } from '@/api/hooks/useGetApiSupervisionTimeCalculations';
import type { GetApiSupervisionTimeCalculationsQueryParams } from '@/api/models/GetApiSupervisionTimeCalculations';

export interface UseSupervisionTimeCalculationsOptions {
  /**
   * Whether to enable the query. Defaults to true when employmentId is provided.
   */
  enabled?: Ref<boolean> | boolean;
  
  /**
   * Stale time in milliseconds. Defaults to 5 minutes.
   */
  staleTime?: number;
  
  /**
   * Whether to refetch on window focus. Defaults to false for this data.
   */
  refetchOnWindowFocus?: boolean;
  
  /**
   * Whether to automatically invalidate based on related data changes.
   */
  autoInvalidate?: boolean;
}

/**
 * Optimized composable for supervision time calculations with smart caching and invalidation.
 * 
 * This hook automatically invalidates when duties, events, or absences change for the employment,
 * ensuring data is always fresh while minimizing unnecessary API calls.
 * 
 * @param employmentId - The employment ID to fetch calculations for
 * @param endDate - Optional end date for the calculations
 * @param options - Configuration options
 */
export function useSupervisionTimeCalculations(
  employmentId: Ref<number | undefined>,
  endDate: Ref<Date | undefined> = computed(() => undefined),
  options: UseSupervisionTimeCalculationsOptions = {}
) {
  const queryClient = useQueryClient();
  
  const {
    enabled = computed(() => !!employmentId.value),
    staleTime = 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus = false,
    autoInvalidate = true
  } = options;

  // Create reactive query parameters
  const queryParams = computed<GetApiSupervisionTimeCalculationsQueryParams>(() => ({
    employment_id: employmentId.value,
    ...(endDate.value && { end_date: endDate.value })
  }));

  // Use the generated TanStack Query hook
  const query = useGetApiSupervisionTimeCalculations(queryParams, {
    query: {
      enabled: enabled as any,
      staleTime,
      refetchOnWindowFocus,
      // Keep data fresh but don't refetch aggressively
      refetchOnMount: false,
      refetchInterval: false,
      // Use background refetch for better UX
      refetchOnReconnect: 'always'
    }
  });

  /**
   * Invalidate supervision time calculations for a specific employment
   */
  const invalidateForEmployment = async (targetEmploymentId: number) => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if ((queryKey[0] as any)?.url === '/api/supervision_time_calculations.json') {
          const params = queryKey[1] as GetApiSupervisionTimeCalculationsQueryParams | undefined;
          return params?.employment_id === targetEmploymentId;
        }
        return false;
      }
    });
  };

  /**
   * Invalidate all supervision time calculations (use sparingly)
   */
  const invalidateAll = async () => {
    await queryClient.invalidateQueries({
      queryKey: [{ url: '/api/supervision_time_calculations.json' }]
    });
  };

  /**
   * Prefetch supervision time calculations for better UX
   */
  const prefetch = async (targetEmploymentId: number, targetEndDate?: Date) => {
    const prefetchParams: GetApiSupervisionTimeCalculationsQueryParams = {
      employment_id: targetEmploymentId,
      ...(targetEndDate && { end_date: targetEndDate })
    };

    await queryClient.prefetchQuery({
      queryKey: getApiSupervisionTimeCalculationsQueryKey(prefetchParams),
      staleTime
    });
  };

  // Set up auto-invalidation when employment changes
  if (autoInvalidate) {
    watch(
      employmentId,
      async (newEmploymentId, oldEmploymentId) => {
        // Invalidate old employment data when switching to a new employment
        if (oldEmploymentId && newEmploymentId !== oldEmploymentId) {
          await invalidateForEmployment(oldEmploymentId);
        }
      },
      { immediate: false }
    );
  }

  return {
    // Query state
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    
    // Utility methods
    invalidateForEmployment,
    invalidateAll,
    prefetch,
    
    // Direct access to query instance for advanced usage
    query
  };
}

/**
 * Global query invalidation utilities for supervision time calculations.
 * Use these when duties, events, or absences are modified.
 */
export class SupervisionTimeCalculationsInvalidator {
  private static queryClient: ReturnType<typeof useQueryClient> | null = null;

  static setQueryClient(client: ReturnType<typeof useQueryClient>) {
    this.queryClient = client;
  }

  static async supervisionTimeCalculationsChanged(employmentId: number) {
    if (!this.queryClient) {
      return;
    }

    await this.queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if ((queryKey[0] as any)?.url === '/api/supervision_time_calculations.json') {
          const params = queryKey[1] as GetApiSupervisionTimeCalculationsQueryParams | undefined;
          const isMatch = params?.employment_id === employmentId;
          return isMatch;
        }
        return false;
      }
    });

    // Also try refetching active queries to ensure immediate updates
    await this.queryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if ((queryKey[0] as any)?.url === '/api/supervision_time_calculations.json') {
          const params = queryKey[1] as GetApiSupervisionTimeCalculationsQueryParams | undefined;
          return params?.employment_id === employmentId;
        }
        return false;
      }
    });
  }

  static async onSupervisionTimeCalculationsChanged(employmentIds: number[]) {
    for (const employmentId of employmentIds) {
      await this.supervisionTimeCalculationsChanged(employmentId);
    }
  }
}